import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Divider,
    TextField,
    Button,
    useTheme,
    Card,
    CardContent,
    Link,
} from '@mui/material';
import { LoginOutlined } from '@mui/icons-material';
import { MicrosoftLoginButton } from './MicrosoftLoginButton';
import { AuthService } from '../../../services/api/auth/AuthService';
import { useAuthContext } from '../../../context';

interface LoginContentProps {
    user: string;
    setUser: (value: string) => void;
    userError: string;
    password: string;
    setPassword: (value: string) => void;
    passwordError: string;
    handleSubmitLogin: () => Promise<void> | void;
    setRecovery: (value: boolean) => void;
    onLoginSuccess?: () => void;
}

export const LoginForm: React.FC<LoginContentProps> = ({
    user,
    setUser,
    userError,
    password,
    setPassword,
    passwordError,
    handleSubmitLogin,
    setRecovery,
    onLoginSuccess,
}) => {
    const theme = useTheme();

    const [loading, setLoading] = useState(false);
    const [formError, setFormError] = useState('');
    const [microsoftLoading, setMicrosoftLoading] = useState(false);

    const { reloadUserData } = useAuthContext();

    useEffect(() => {
        const checkMicrosoftToken = async () => {
            const urlParams = new URLSearchParams(window.location.search);
            const microsoftToken = urlParams.get('microsoft_token');
            const error = urlParams.get('error');

            if (error) {
                setFormError(`Erro no login Microsoft: ${error}`);
                window.history.replaceState({}, document.title, window.location.pathname);
                return;
            }

            if (microsoftToken) {
                setMicrosoftLoading(true);
                try {
                    const result = await AuthService.processMicrosoftToken(microsoftToken);
                    if (result === true) {

                        await reloadUserData();


                        onLoginSuccess?.();
                    } else if (result instanceof Error) {
                        setFormError(result.message);
                    }
                } catch (error) {
                    setFormError('Erro ao processar login com Microsoft');
                } finally {
                    setMicrosoftLoading(false);
                    window.history.replaceState({}, document.title, window.location.pathname);
                }
            }
        };

        checkMicrosoftToken();
    }, [onLoginSuccess, reloadUserData]);

    const validate = () => {
        if (!user.trim()) {
            setFormError('Por favor, informe o usuário');
            return false;
        }
        if (!password.trim()) {
            setFormError('Por favor, informe a senha');
            return false;
        }
        setFormError('');
        return true;
    };

    const onFormSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!validate()) return;

        setLoading(true);
        try {
            await handleSubmitLogin();
        } catch (error) {
            setFormError('Erro ao tentar fazer login. Tente novamente.');
        } finally {
            setLoading(false);
        }
    };

    if (microsoftLoading) {
        return (
            <Card>
                <CardContent>
                    <Box padding={1} display="flex" flexDirection="column" alignItems="center" gap={2}>
                        <Typography variant="h6">Processando login com Microsoft...</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Aguarde enquanto validamos suas credenciais
                        </Typography>
                    </Box>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card>
            <CardContent>
                <Box padding={1}>
                    <form onSubmit={onFormSubmit} noValidate>
                        <Box display="flex" flexDirection="column" gap={2} width={250}>
                            <Box>
                                <Typography variant="h4" align="left" color={theme.palette.primary.main}>
                                    HTI
                                </Typography>
                                <Typography variant="body1" align="left">
                                    Sistema de Gestão Integrada de Barragens
                                </Typography>
                                <Divider
                                    flexItem
                                    variant="fullWidth"
                                    orientation="horizontal"
                                    color={theme.palette.primary.main}
                                />
                            </Box>

                            <TextField
                                autoFocus
                                fullWidth
                                label="Usuário"
                                value={user}
                                error={!!userError}
                                helperText={userError}
                                onChange={(e) => setUser(e.target.value)}
                                disabled={loading}
                                inputProps={{ 'aria-label': 'Usuário' }}
                            />

                            <TextField
                                fullWidth
                                label="Senha"
                                type="password"
                                value={password}
                                error={!!passwordError}
                                helperText={passwordError}
                                onChange={(e) => setPassword(e.target.value)}
                                disabled={loading}
                                inputProps={{ 'aria-label': 'Senha' }}
                            />

                            {formError && (
                                <Typography color="error" variant="body2" role="alert">
                                    {formError}
                                </Typography>
                            )}
                        </Box>

                        <Box width="100%" display="flex" justifyContent="center" marginTop={2}>
                            <Button
                                startIcon={<LoginOutlined />}
                                variant="contained"
                                fullWidth
                                type="submit"
                                disabled={loading}
                                aria-busy={loading}
                            >
                                {loading ? 'Entrando...' : 'ENTRAR'}
                            </Button>
                        </Box>

                        <Box width="100%" display="flex" justifyContent="center" marginTop={2}>
                            <MicrosoftLoginButton />
                        </Box>

                        <Box justifyContent="end" display="flex" width="100%" mt={1}>
                            <Link
                                component="button"
                                variant="body2"
                                onClick={() => setRecovery(true)}
                                disabled={loading}
                                sx={{ cursor: loading ? 'not-allowed' : 'pointer' }}
                                tabIndex={loading ? -1 : 0}
                            >
                                Esqueci minha senha
                            </Link>
                        </Box>


                    </form>
                </Box>
            </CardContent>
        </Card>
    );
};